import _ from 'lodash';
/**
 * A type guard that checks if the given node is a leaf node.
 * @param node The node to check.
 */
export var isLeafNode = function (node) {
    return !('children' in node);
};
/**
 * A type guard that checks if the given node is a branch node.
 * @param node The node to check.
 */
export var isBranchNode = function (node) { return 'children' in node; };
/**
 * Returns a flat array with all child nodes of the given node. This will be a combination of branch and leaf nodes.
 *
 * @param node The node to get the children of.
 */
export var getChildNodes = function (node) {
    var nodes = [];
    var getChildNodesInternal = function (node) {
        nodes.push(node);
        if (isBranchNode(node)) {
            node.children.forEach(getChildNodesInternal);
        }
    };
    if (isBranchNode(node)) {
        node.children.forEach(getChildNodesInternal);
    }
    return nodes;
};
/**
 * Returns a flat array with all leaf nodes of the given node.
 *
 * @param node The node to get the children of.
 */
export var getLeafNodes = function (node) {
    var nodes = [];
    var getChildNodesInternal = function (node) {
        if (isBranchNode(node)) {
            node.children.forEach(getChildNodesInternal);
        }
        else {
            nodes.push(node);
        }
    };
    if (isBranchNode(node)) {
        node.children.forEach(getChildNodesInternal);
    }
    return nodes;
};
/**
 * Returns a flat array with all parent nodes of the given node. The given `node` is **not** included in the array.
 *
 * @param node The node to get the parent nodes of.
 * @param parentByKey A map containing all nodes, with the key being the node key and the value being the parent node.
 */
export var getParentNodes = function (node, parentByKey) {
    var nodes = [];
    var getParentNodesInternal = function (node) {
        var parent = parentByKey.get(node.key);
        if (parent) {
            nodes.push(parent);
            getParentNodesInternal(parent);
        }
    };
    getParentNodesInternal(node);
    return nodes;
};
/**
 * Flattens the given array of nodes. This means branch nodes are expanded into their children, and the resulting array
 * will contain every single node in a flat array.
 *
 * @param nodes An array of nodes to flatten.
 */
export var flattenNodes = function (nodes) {
    var flattened = [];
    var flattenNodesInternal = function (node) {
        flattened.push(node);
        if (isBranchNode(node)) {
            flattened.push.apply(flattened, getChildNodes(node));
        }
    };
    nodes.forEach(function (node) { return flattenNodesInternal(node); });
    return flattened;
};
/**
 * Checks whether at least one of the children of the given node is selected. If the given node is a leaf, returns false.
 * @param node The node to check.
 * @param selectedKeys A map with all selected keys.
 */
export var someChildIsSelected = function (node, selectedKeys) {
    return isBranchNode(node) &&
        node.children.some(function (child) { return selectedKeys.get(child.key) || someChildIsSelected(child, selectedKeys); });
};
/**
 * Checks whether every child (recursively) of the given node is selected. If the given node is a leaf, returns false.
 * @param node The node to check.
 * @param selectedKeys A map with all selected keys.
 */
export var everyChildIsSelected = function (node, selectedKeys) {
    return isBranchNode(node) &&
        node.children.every(function (child) { return selectedKeys.get(child.key) || everyChildIsSelected(child, selectedKeys); });
};
/**
 * Computes the selected keys based on the given nodes, the selected keys, and the branch selection mode. Depending on
 * the branch selection mode, more nodes might be selected than what is already in `selected`.
 */
export var computeSelectedKeys = function (nodes, selected, branchSelectionMode) {
    var selectedInternal = new Map(selected);
    if (branchSelectionMode === 'branch') {
        // When the branch selection mode is "branch", the branches themselves are not marked as selected in the
        //  internal state, only their children. When invoking the `onChange` callback, we want to include the
        //  branches in the list of selected keys, as well. That is, only the branches for which **all** child
        //  nodes are selected.
        var includeSelectedParentNodes_1 = function (node) {
            if (isBranchNode(node)) {
                if (everyChildIsSelected(node, selected)) {
                    selectedInternal.set(node.key, true);
                }
                else {
                    selectedInternal.delete(node.key);
                }
                _.forEach(node.children, includeSelectedParentNodes_1);
            }
        };
        _.forEach(nodes, includeSelectedParentNodes_1);
    }
    else if (branchSelectionMode === 'none') {
        // When the branch selection mode is "none", branches are not included in the selected keys. The following
        //  code excludes all branch nodes from the selected keys. Branch nodes can end up being selected when the
        //  consumer changes the `branchSelectionMode` dynamically and there were branch nodes selected prior.
        var excludeBranchNodes_1 = function (node) {
            if (isBranchNode(node)) {
                selectedInternal.delete(node.key);
                _.forEach(node.children, excludeBranchNodes_1);
            }
        };
        _.forEach(nodes, excludeBranchNodes_1);
    }
    return Array.from(selectedInternal.keys());
};
/**
 * Computes the resulting selected keys when selecting the given `key`. This function takes into account the current
 * selection, whether the tree view is in multi select mode, and the branch selection mode. Returns an object with the
 * resulting selected keys, as well as all keys that should be selected and unselected as a result of selecting the
 * given `key`.
 */
export var selectKey = function (key, nodes, nodesByKey, selectedKeys, isMultiSelect, branchSelectionMode) {
    var _a;
    var node = nodesByKey.get(key);
    var prevSelectedKeys = Array.from(selectedKeys.keys());
    /**
     * Store all the nodes that will end up being selected by selecting the given `key`. This is at the very least the
     * node with the given `key`, but in multi select mode and branch selection mode "branch", it might also include
     * all the children of the node.
     */
    var newSelectedKeys = [node.key];
    /** Stores all the nodes that might end up being unselected by selecting the given `key`. */
    var newUnselectedKeys = [];
    if (isMultiSelect) {
        // In multi select mode we need to watch out for the "branch" selection mode; in that mode, selecting a node
        //  that turns out to be a "branch" node means that we need to also select all of its children, as well.
        if (branchSelectionMode === 'branch') {
            if (isBranchNode(node)) {
                newSelectedKeys.push.apply(newSelectedKeys, _.map(getChildNodes(node), 'key'));
            }
        }
        else if (branchSelectionMode === 'branch-visual') {
            // "visual-branch" mode is similar, except that it won't select the branch itself, only the children.
            if (isBranchNode(node)) {
                newSelectedKeys = _.map(getLeafNodes(node), 'key');
            }
        }
    }
    else {
        // In single select mode we need to unselect all previously selected nodes and only select the new one.
        newUnselectedKeys.push.apply(newUnselectedKeys, prevSelectedKeys);
    }
    var preliminarySelectedKeys = (_a = _(prevSelectedKeys)
        .concat(newSelectedKeys))
        .without.apply(_a, newUnselectedKeys).reduce(function (map, key) { return map.set(key, true); }, new Map());
    var finalSelectedKeys = computeSelectedKeys(nodes, preliminarySelectedKeys, branchSelectionMode);
    var finalNewSelectedKeys = _.difference(finalSelectedKeys, prevSelectedKeys);
    var finalNewUnselectedKeys = _.difference(prevSelectedKeys, finalSelectedKeys);
    return {
        selectedKeys: finalSelectedKeys,
        newSelectedKeys: finalNewSelectedKeys,
        newUnselectedKeys: finalNewUnselectedKeys,
    };
};
/**
 * Computes the resulting selected keys when unselecting the given `key`. This function takes into account the current
 * selection, whether the tree view is in multi select mode, and the branch selection mode. Returns an object with the
 * resulting selected keys and all keys that should be unselected as a result of unselecting the given `key`.
 */
export var unselectKey = function (key, nodes, nodesByKey, selectedKeys, isMultiSelect, branchSelectionMode) {
    var _a;
    var node = nodesByKey.get(key);
    var prevSelectedKeys = Array.from(selectedKeys.keys());
    /** Stores all the nodes that might end up being unselected by unselecting the given `key`. */
    var newUnselectedKeys = [node.key];
    if (isMultiSelect) {
        // In multi select mode we need to watch out for the "branch" selection mode; in that mode, unselecting a node
        //  that turns out to be a "branch" node means that we need to also unselect all of its children, as well.
        if ((branchSelectionMode === 'branch' || branchSelectionMode === 'branch-visual') && isBranchNode(node)) {
            newUnselectedKeys.push.apply(newUnselectedKeys, _.map(getChildNodes(node), 'key'));
        }
    }
    else {
        // In single select mode we need to unselect all previously selected nodes
        newUnselectedKeys.push.apply(newUnselectedKeys, prevSelectedKeys);
    }
    var preliminarySelectedKeys = (_a = _(prevSelectedKeys))
        .without.apply(_a, newUnselectedKeys).reduce(function (map, key) { return map.set(key, true); }, new Map());
    var finalSelectedKeys = computeSelectedKeys(nodes, preliminarySelectedKeys, branchSelectionMode);
    var finalNewUnselectedKeys = _.difference(prevSelectedKeys, finalSelectedKeys);
    return {
        selectedKeys: finalSelectedKeys,
        newUnselectedKeys: finalNewUnselectedKeys,
    };
};
/**
 * Creates a map with the given nodes as values and their keys as keys.
 */
export var createNodesByKeyMap = function (nodes) {
    var map = new Map();
    var traverse = function (node) {
        map.set(node.key, node);
        if (isBranchNode(node)) {
            node.children.forEach(traverse);
        }
    };
    nodes.forEach(traverse);
    return map;
};
/**
 * Creates a map with the given nodes as values and their parent nodes as values. The root nodes will have `null` as
 * their parent.
 */
export var createParentByKeyMap = function (nodes) {
    var map = new Map();
    var traverse = function (node, parent) {
        if (parent === void 0) { parent = null; }
        map.set(node.key, parent);
        if (isBranchNode(node)) {
            node.children.forEach(function (child) { return traverse(child, node); });
        }
    };
    nodes.forEach(function (node) { return traverse(node); });
    return map;
};
/**
 * Returns a map with the given keys as keys and `true` as values.
 */
export var toTrueByKeyMap = function (keys) {
    return _.reduce(keys, function (map, key) { return map.set(key, true); }, new Map());
};
/**
 * Maps the given keys to the corresponding nodes in the given map. Use to convert an array of keys to an array of
 * their corresponding nodes.
 */
export var mapKeysToNodes = function (keys, nodesByKey) { return _.map(keys, function (key) { return nodesByKey.get(key); }); };
